import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const AdminAnalyticsPage = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [userGrowth, setUserGrowth] = useState([]);
  const [subscriptionTrend, setSubscriptionTrend] = useState([]);
  const [featureUsage, setFeatureUsage] = useState([]);
  const [engagement, setEngagement] = useState(null);
  const [personaGrowth, setPersonaGrowth] = useState([]);
  const [personaConnections, setPersonaConnections] = useState(null);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    loadAnalytics();
  }, [isAdmin]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      // Load all analytics data
      const [overviewRes, growthRes, subscriptionRes, featureRes, engagementRes, personaGrowthRes, personaConnectionsRes] = await Promise.all([
        axios.get('/analytics/overview'),
        axios.get('/analytics/user-growth'),
        axios.get('/analytics/subscription-trend'),
        axios.get('/analytics/feature-usage'),
        axios.get('/analytics/engagement'),
        axios.get('/analytics/persona-growth'),
        axios.get('/analytics/persona-connections')
      ]);

      setOverview(overviewRes.data);
      setUserGrowth(growthRes.data);
      setSubscriptionTrend(subscriptionRes.data);
      setFeatureUsage(featureRes.data.slice(0, 10)); // Top 10 features
      setEngagement(engagementRes.data);
      setPersonaGrowth(personaGrowthRes.data);
      setPersonaConnections(personaConnectionsRes.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
      alert('Failed to load analytics: ' + (error.response?.data?.error || error.message));
    }
    setLoading(false);
  };

  if (!isAdmin) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>You don't have permission to access this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{
        padding: '2rem',
        paddingTop: '5rem',
        textAlign: 'center',
        background: 'linear-gradient(135deg, #141517 0%, #1f2023 100%)',
        minHeight: '100vh',
        color: '#9ca3af'
      }}>
        Loading analytics...
      </div>
    );
  }

  const calculateTrend = (current, previous) => {
    if (!previous || previous === 0) return 0;
    return (((current - previous) / previous) * 100).toFixed(1);
  };

  return (
    <div style={{
      padding: '2rem',
      paddingTop: '5rem',
      maxWidth: '1400px',
      margin: '0 auto',
      background: 'linear-gradient(135deg, #141517 0%, #1f2023 100%)',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{
            fontSize: '2.5rem',
            background: 'linear-gradient(135deg, #8b5cf6 0%, #d946ef 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            marginBottom: '0.5rem'
          }}>
            📊 Analytics Dashboard
          </h1>
          <p style={{ color: '#9ca3af', fontSize: '1.1rem' }}>
            Real-time platform metrics and insights
          </p>
        </div>
        <button
          onClick={loadAnalytics}
          style={{
            padding: '0.75rem 1.5rem',
            background: 'linear-gradient(135deg, #8b5cf6 0%, #d946ef 100%)',
            border: 'none',
            borderRadius: '8px',
            color: 'white',
            fontWeight: 'bold',
            cursor: 'pointer',
            fontSize: '1rem'
          }}
        >
          🔄 Refresh
        </button>
      </div>

      {overview && (
        <>
          {/* Key Metrics Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1rem',
            marginBottom: '2rem'
          }}>
            {/* Total Users */}
            <div style={{
              background: 'rgba(139, 92, 246, 0.1)',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              borderRadius: '12px',
              padding: '1.5rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Total Users</span>
                <span style={{ fontSize: '1.5rem' }}>👥</span>
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#8b5cf6', marginBottom: '0.25rem' }}>
                {overview.total_users || 0}
              </div>
              <div style={{ color: '#22c55e', fontSize: '0.85rem' }}>
                +{overview.new_users_month || 0} this month
              </div>
            </div>

            {/* Active Today (DAU) */}
            <div style={{
              background: 'rgba(217, 70, 239, 0.1)',
              border: '1px solid rgba(217, 70, 239, 0.3)',
              borderRadius: '12px',
              padding: '1.5rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Active Today</span>
                <span style={{ fontSize: '1.5rem' }}>🔥</span>
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#d946ef', marginBottom: '0.25rem' }}>
                {overview.dau || 0}
              </div>
              <div style={{ color: '#9ca3af', fontSize: '0.85rem' }}>
                DAU • {overview.mau || 0} MAU
              </div>
            </div>

            {/* MRR */}
            <div style={{
              background: 'rgba(34, 197, 94, 0.1)',
              border: '1px solid rgba(34, 197, 94, 0.3)',
              borderRadius: '12px',
              padding: '1.5rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Monthly Revenue</span>
                <span style={{ fontSize: '1.5rem' }}>💰</span>
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#22c55e', marginBottom: '0.25rem' }}>
                ${parseFloat(overview.mrr || 0).toFixed(2)}
              </div>
              <div style={{ color: '#9ca3af', fontSize: '0.85rem' }}>
                MRR • {overview.pro_users || 0} Pro users
              </div>
            </div>

            {/* Conversion Rate */}
            <div style={{
              background: 'rgba(234, 179, 8, 0.1)',
              border: '1px solid rgba(234, 179, 8, 0.3)',
              borderRadius: '12px',
              padding: '1.5rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Conversion Rate</span>
                <span style={{ fontSize: '1.5rem' }}>📈</span>
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#eab308', marginBottom: '0.25rem' }}>
                {parseFloat(overview.conversion_rate || 0).toFixed(1)}%
              </div>
              <div style={{ color: '#9ca3af', fontSize: '0.85rem' }}>
                Free → Pro conversion
              </div>
            </div>
          </div>

          {/* Charts Row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
            gap: '1.5rem',
            marginBottom: '2rem'
          }}>
            {/* User Growth Chart */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              padding: '1.5rem'
            }}>
              <h3 style={{ color: '#8b5cf6', marginBottom: '1rem', fontSize: '1.2rem' }}>
                📈 User Growth (30 Days)
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={userGrowth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#9ca3af"
                    tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis stroke="#9ca3af" />
                  <Tooltip 
                    contentStyle={{ background: '#141517', border: '1px solid rgba(139, 92, 246, 0.3)', borderRadius: '8px' }}
                    labelStyle={{ color: '#9ca3af' }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="new_users" stroke="#8b5cf6" strokeWidth={2} name="New Users" />
                  <Line type="monotone" dataKey="cumulative_users" stroke="#d946ef" strokeWidth={2} name="Total Users" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Revenue Trend Chart */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              padding: '1.5rem'
            }}>
              <h3 style={{ color: '#22c55e', marginBottom: '1rem', fontSize: '1.2rem' }}>
                💰 Revenue Trend (30 Days)
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={subscriptionTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#9ca3af"
                    tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis stroke="#9ca3af" />
                  <Tooltip 
                    contentStyle={{ background: '#141517', border: '1px solid rgba(34, 197, 94, 0.3)', borderRadius: '8px' }}
                    labelStyle={{ color: '#9ca3af' }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="daily_mrr" stroke="#22c55e" strokeWidth={2} name="MRR ($)" />
                  <Line type="monotone" dataKey="pro_users" stroke="#eab308" strokeWidth={2} name="Pro Users" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Feature Usage & Engagement */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))',
            gap: '1.5rem',
            marginBottom: '2rem'
          }}>
            {/* Top Features */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              padding: '1.5rem'
            }}>
              <h3 style={{ color: '#d946ef', marginBottom: '1rem', fontSize: '1.2rem' }}>
                🎯 Top Features
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={featureUsage} layout="horizontal">
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis type="number" stroke="#9ca3af" />
                  <YAxis 
                    dataKey="feature_name" 
                    type="category" 
                    width={150}
                    stroke="#9ca3af"
                    style={{ fontSize: '0.8rem' }}
                  />
                  <Tooltip 
                    contentStyle={{ background: '#141517', border: '1px solid rgba(217, 70, 239, 0.3)', borderRadius: '8px' }}
                  />
                  <Bar dataKey="unique_users" fill="#d946ef" name="Users" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Engagement & Quick Stats */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              padding: '1.5rem'
            }}>
              <h3 style={{ color: '#3b82f6', marginBottom: '1rem', fontSize: '1.2rem' }}>
                📊 Engagement & Stats
              </h3>

              {/* Stickiness Gauge */}
              {engagement && (
                <div style={{
                  background: 'rgba(59, 130, 246, 0.1)',
                  borderRadius: '8px',
                  padding: '1.5rem',
                  marginBottom: '1rem',
                  textAlign: 'center'
                }}>
                  <div style={{ color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                    DAU/MAU Stickiness
                  </div>
                  <div style={{ fontSize: '3rem', fontWeight: 'bold', color: '#3b82f6' }}>
                    {engagement.stickiness}%
                  </div>
                  <div style={{ color: '#9ca3af', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                    {engagement.dau} daily / {engagement.mau} monthly
                  </div>
                </div>
              )}

              {/* Quick Stats */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.75rem',
                  background: 'rgba(139, 92, 246, 0.1)',
                  borderRadius: '8px'
                }}>
                  <span style={{ color: '#9ca3af' }}>Users with Personas</span>
                  <span style={{ color: '#8b5cf6', fontWeight: 'bold' }}>{overview.users_with_personas || 0}</span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.75rem',
                  background: 'rgba(217, 70, 239, 0.1)',
                  borderRadius: '8px'
                }}>
                  <span style={{ color: '#9ca3af' }}>Users in Groups</span>
                  <span style={{ color: '#d946ef', fontWeight: 'bold' }}>{overview.users_with_groups || 0}</span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.75rem',
                  background: 'rgba(34, 197, 94, 0.1)',
                  borderRadius: '8px'
                }}>
                  <span style={{ color: '#9ca3af' }}>Users Searching</span>
                  <span style={{ color: '#22c55e', fontWeight: 'bold' }}>{overview.users_searching || 0}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Persona Network Stats */}
          {personaConnections && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '1.5rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{
                background: 'rgba(6, 182, 212, 0.1)',
                border: '1px solid rgba(6, 182, 212, 0.3)',
                borderRadius: '12px',
                padding: '1.5rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Total Personas</span>
                  <span style={{ fontSize: '1.5rem' }}>🎭</span>
                </div>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#06b6d4' }}>
                  {personaConnections.total_personas}
                </div>
              </div>

              <div style={{
                background: 'rgba(6, 182, 212, 0.1)',
                border: '1px solid rgba(6, 182, 212, 0.3)',
                borderRadius: '12px',
                padding: '1.5rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Connected Personas</span>
                  <span style={{ fontSize: '1.5rem' }}>🔗</span>
                </div>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#06b6d4' }}>
                  {personaConnections.connected_personas}
                </div>
                <div style={{ color: '#9ca3af', fontSize: '0.85rem' }}>
                  {personaConnections.total_personas > 0
                    ? `${((personaConnections.connected_personas / personaConnections.total_personas) * 100).toFixed(0)}% share a trait with someone else`
                    : 'share a trait with someone else'}
                </div>
              </div>

              <div style={{
                background: 'rgba(6, 182, 212, 0.1)',
                border: '1px solid rgba(6, 182, 212, 0.3)',
                borderRadius: '12px',
                padding: '1.5rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Connections</span>
                  <span style={{ fontSize: '1.5rem' }}>🕸️</span>
                </div>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#06b6d4' }}>
                  {personaConnections.connected_pairs}
                </div>
                <div style={{ color: '#9ca3af', fontSize: '0.85rem' }}>
                  persona pairs sharing a trait
                </div>
              </div>

              <div style={{
                background: 'rgba(6, 182, 212, 0.1)',
                border: '1px solid rgba(6, 182, 212, 0.3)',
                borderRadius: '12px',
                padding: '1.5rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Traits in Use</span>
                  <span style={{ fontSize: '1.5rem' }}>🏷️</span>
                </div>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#06b6d4' }}>
                  {personaConnections.total_tags}
                </div>
                <div style={{ color: '#9ca3af', fontSize: '0.85rem' }}>
                  {personaConnections.shared_tags} shared • {personaConnections.unique_tags} one-of-a-kind
                </div>
              </div>
            </div>
          )}

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
            gap: '1.5rem',
            marginBottom: '2rem'
          }}>
            {/* Persona Growth Chart */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              padding: '1.5rem'
            }}>
              <h3 style={{ color: '#06b6d4', marginBottom: '1rem', fontSize: '1.2rem' }}>
                🎭 Persona Growth
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={personaGrowth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis
                    dataKey="date"
                    stroke="#9ca3af"
                    tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis stroke="#9ca3af" />
                  <Tooltip
                    contentStyle={{ background: '#141517', border: '1px solid rgba(6, 182, 212, 0.3)', borderRadius: '8px' }}
                    labelStyle={{ color: '#9ca3af' }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="new_personas" stroke="#06b6d4" strokeWidth={2} name="New Personas" />
                  <Line type="monotone" dataKey="cumulative_personas" stroke="#8b5cf6" strokeWidth={2} name="Total Personas" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Most Shared Traits */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              padding: '1.5rem'
            }}>
              <h3 style={{ color: '#06b6d4', marginBottom: '1rem', fontSize: '1.2rem' }}>
                🌟 Most Shared Traits
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={personaConnections?.top_shared_tags || []} layout="horizontal">
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis type="number" stroke="#9ca3af" allowDecimals={false} />
                  <YAxis
                    dataKey="tag"
                    type="category"
                    width={150}
                    stroke="#9ca3af"
                    style={{ fontSize: '0.8rem' }}
                  />
                  <Tooltip
                    contentStyle={{ background: '#141517', border: '1px solid rgba(6, 182, 212, 0.3)', borderRadius: '8px' }}
                  />
                  <Bar dataKey="connections" fill="#06b6d4" name="Personas/Users" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Info Panel */}
          <div style={{
            background: 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '12px',
            padding: '1.5rem'
          }}>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <span style={{ fontSize: '1.5rem' }}>ℹ️</span>
              <div>
                <h4 style={{ color: '#3b82f6', marginBottom: '0.5rem', fontSize: '1.1rem' }}>
                  About Analytics
                </h4>
                <div style={{ color: '#9ca3af', lineHeight: '1.6', fontSize: '0.95rem' }}>
                  <div>• Data updates in real-time as users interact with the platform</div>
                  <div>• DAU = Daily Active Users, MAU = Monthly Active Users</div>
                  <div>• Stickiness (DAU/MAU ratio) indicates user engagement quality</div>
                  <div>• MRR calculated as Pro users × $9.99/month</div>
                  <div>• Last updated: {new Date(overview.calculated_at).toLocaleString()}</div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminAnalyticsPage;
